
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus, ShoppingCart, Search, Filter, ExternalLink } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface LetsOrderProps {
  onAddToCart: (quantity: number) => void;
}

interface Product {
  id: number;
  name: string;
  sku: string;
  brand: string;
  category: string;
  sellerName: string; // Changed from subcategory to sellerName
  packSize: string;
  priceTier: "Normal" | "Promo" | "Subsidy";
  moq: number;
  availableStock: number;
  unitPrice: number;
  deliveryTime: "Standard" | "Urgent" | "Pre-order";
}

const LetsOrder: React.FC<LetsOrderProps> = ({ onAddToCart }) => {
  // Sample data - in a real app this would come from an API
  const products: Product[] = [
    {
      id: 1,
      name: "Fresh Milk",
      sku: "MILK-001",
      brand: "Farm Fresh",
      category: "Dairy",
      sellerName: "Farm Fresh Distributor", // Changed from subcategory
      packSize: "1L",
      priceTier: "Normal",
      moq: 5,
      availableStock: 120,
      unitPrice: 6.90,
      deliveryTime: "Standard"
    },
    {
      id: 2,
      name: "White Bread",
      sku: "BREAD-001",
      brand: "Gardenia",
      category: "Bakery",
      sellerName: "Gardenia Foods", // Changed from subcategory
      packSize: "400g",
      priceTier: "Normal",
      moq: 10,
      availableStock: 85,
      unitPrice: 3.50,
      deliveryTime: "Standard"
    },
    {
      id: 3,
      name: "Eggs",
      sku: "EGG-001",
      brand: "Farm Best",
      category: "Dairy",
      sellerName: "Poultry Suppliers", // Changed from subcategory
      packSize: "10pcs",
      priceTier: "Subsidy",
      moq: 5,
      availableStock: 45,
      unitPrice: 5.80,
      deliveryTime: "Standard"
    },
    {
      id: 4,
      name: "Milo",
      sku: "BEV-001",
      brand: "Nestlé",
      category: "Beverages",
      sellerName: "Nestlé Malaysia", // Changed from subcategory
      packSize: "1kg",
      priceTier: "Promo",
      moq: 3,
      availableStock: 50,
      unitPrice: 23.90,
      deliveryTime: "Standard"
    },
    {
      id: 5,
      name: "Cooking Oil",
      sku: "OIL-001",
      brand: "Knife",
      category: "Cooking Essentials",
      sellerName: "Lam Soon", // Changed from subcategory
      packSize: "5kg",
      priceTier: "Subsidy",
      moq: 2,
      availableStock: 30,
      unitPrice: 28.90,
      deliveryTime: "Standard"
    },
    {
      id: 6,
      name: "Rice",
      sku: "RICE-001",
      brand: "Jasmine",
      category: "Cooking Essentials",
      sellerName: "Padiberas Nasional", // Changed from subcategory
      packSize: "10kg",
      priceTier: "Subsidy",
      moq: 1,
      availableStock: 60,
      unitPrice: 45.90,
      deliveryTime: "Standard"
    },
    {
      id: 7,
      name: "Canned Tuna",
      sku: "CAN-001",
      brand: "Ayam Brand",
      category: "Canned Food",
      sellerName: "Ayam Brand Malaysia", // Changed from subcategory
      packSize: "185g",
      priceTier: "Promo",
      moq: 12,
      availableStock: 100,
      unitPrice: 9.90,
      deliveryTime: "Standard"
    }
  ];

  const categories = Array.from(new Set(products.map(p => p.category)));
  const sellerNames = Array.from(new Set(products.map(p => p.sellerName))); // Changed from subcategories

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSellerName, setSelectedSellerName] = useState(""); // Changed from selectedSubcategory
  const [filters, setFilters] = useState({
    promo: false,
    fastMoving: false,
    subsidy: false
  });
  
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [selectedProducts, setSelectedProducts] = useState<Array<{id: number, quantity: number}>>([]);

  const filteredProducts = products.filter(product => {
    // Search filter
    const matchesSearch = searchTerm === "" || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Category filter
    const matchesCategory = selectedCategory === "" || product.category === selectedCategory;
    
    // Seller Name filter (changed from subcategory)
    const matchesSellerName = selectedSellerName === "" || product.sellerName === selectedSellerName;
    
    // Other filters
    const matchesPromo = !filters.promo || product.priceTier === "Promo";
    const matchesSubsidy = !filters.subsidy || product.priceTier === "Subsidy";
    
    // Fast moving is mocked here - in real app would be based on sales data
    const isFastMoving = [2, 4, 7].includes(product.id);
    const matchesFastMoving = !filters.fastMoving || isFastMoving;
    
    return matchesSearch && matchesCategory && matchesSellerName && 
           matchesPromo && matchesFastMoving && matchesSubsidy;
  });

  const handleQuantityChange = (id: number, value: number) => {
    setQuantities(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleAddToCart = (product: Product) => {
    const quantity = quantities[product.id] || 0;
    if (quantity > 0) {
      onAddToCart(quantity);
      setSelectedProducts(prev => [...prev, { id: product.id, quantity }]);
      // Reset quantity
      setQuantities(prev => ({
        ...prev,
        [product.id]: 0
      }));
    }
  };

  const totalUnits = selectedProducts.reduce((sum, item) => sum + item.quantity, 0);
  const totalValue = selectedProducts.reduce((sum, item) => {
    const product = products.find(p => p.id === item.id);
    return sum + (product ? product.unitPrice * item.quantity : 0);
  }, 0);

  return (
    <div className="space-y-6">
      <Button 
        className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-2"
        onClick={() => window.open("https://v2.jimatlabs.com/products", "_blank")}
      >
        Browse more at JiMATBiz
        <ExternalLink className="h-4 w-4" />
      </Button>
      
      <Tabs defaultValue="browse-all" className="w-full">
        <TabsList className="bg-green-50">
          <TabsTrigger value="browse-all">Browse All</TabsTrigger>
          <TabsTrigger value="by-category">By Category</TabsTrigger>
          <TabsTrigger value="by-seller">By Seller</TabsTrigger> {/* Changed from by-subcategory */}
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="filters">Filters</TabsTrigger>
        </TabsList>
        
        <div className="flex mt-4 space-x-4">
          <Card className="flex-1 border-green-100">
            <CardContent className="p-4">
              <TabsContent value="browse-all" className="mt-0">
                <div className="space-y-4">
                  <h4 className="font-medium">All Products</h4>
                </div>
              </TabsContent>
              
              <TabsContent value="by-category" className="mt-0">
                <div className="space-y-4">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Categories</SelectLabel>
                        <SelectItem value="">All Categories</SelectItem>
                        {categories.map(category => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
              
              <TabsContent value="by-seller" className="mt-0"> {/* Changed from by-subcategory */}
                <div className="space-y-4">
                  <Select value={selectedSellerName} onValueChange={setSelectedSellerName}> {/* Changed from selectedSubcategory */}
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select seller" /> {/* Changed from subcategory */}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Sellers</SelectLabel> {/* Changed from Subcategories */}
                        <SelectItem value="">All Sellers</SelectItem> {/* Changed from All Subcategories */}
                        {sellerNames.map(sellerName => ( /* Changed from subcategories */
                          <SelectItem key={sellerName} value={sellerName}>{sellerName}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
              
              <TabsContent value="search" className="mt-0">
                <div className="flex w-full max-w-sm items-center space-x-2">
                  <Input
                    type="text"
                    placeholder="Search by name, SKU, or brand..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="filters" className="mt-0">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="promo" 
                        checked={filters.promo}
                        onCheckedChange={(checked) => 
                          setFilters(prev => ({ ...prev, promo: checked === true }))
                        }
                      />
                      <Label htmlFor="promo">Promo items</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="fastMoving" 
                        checked={filters.fastMoving}
                        onCheckedChange={(checked) => 
                          setFilters(prev => ({ ...prev, fastMoving: checked === true }))
                        }
                      />
                      <Label htmlFor="fastMoving">Fast-Moving</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="subsidy" 
                        checked={filters.subsidy}
                        onCheckedChange={(checked) => 
                          setFilters(prev => ({ ...prev, subsidy: checked === true }))
                        }
                      />
                      <Label htmlFor="subsidy">Subsidy only</Label>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <div className="mt-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-green-50">
                        <TableHead>Product Name</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Brand</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Seller's Name</TableHead> {/* Changed from Sub-category */}
                        <TableHead>Pack Size</TableHead>
                        <TableHead>Price Tier</TableHead>
                        <TableHead>MOQ</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Available Stock</TableHead>
                        <TableHead>Unit Price (RM)</TableHead>
                        <TableHead>Delivery Time</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>{product.sku}</TableCell>
                          <TableCell>{product.brand}</TableCell>
                          <TableCell>{product.category}</TableCell>
                          <TableCell>{product.sellerName}</TableCell> {/* Changed from subcategory */}
                          <TableCell>{product.packSize}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              product.priceTier === 'Promo' ? 'bg-amber-100 text-amber-800' : 
                              product.priceTier === 'Subsidy' ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {product.priceTier}
                            </span>
                          </TableCell>
                          <TableCell>{product.moq}</TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Input
                                type="number"
                                min={0}
                                value={quantities[product.id] || 0}
                                onChange={(e) => handleQuantityChange(product.id, parseInt(e.target.value) || 0)}
                                className="w-16"
                              />
                            </div>
                          </TableCell>
                          <TableCell>{product.availableStock}</TableCell>
                          <TableCell>{product.unitPrice.toFixed(2)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              product.deliveryTime === 'Urgent' ? 'bg-red-100 text-red-800' : 
                              product.deliveryTime === 'Pre-order' ? 'bg-purple-100 text-purple-800' : 
                              'bg-green-100 text-green-800'
                            }`}>
                              {product.deliveryTime}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleAddToCart(product)}
                              disabled={(quantities[product.id] || 0) === 0}
                            >
                              <ShoppingCart className="mr-2 h-4 w-4" />
                              Add
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="w-1/4">
            <Card className="border-green-100">
              <CardContent className="p-4">
                <h4 className="font-medium mb-4">Selected Products</h4>
                {selectedProducts.length > 0 ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      {selectedProducts.map((item) => {
                        const product = products.find(p => p.id === item.id);
                        return product ? (
                          <div key={item.id} className="flex justify-between items-center text-sm">
                            <span>{product.name} × {item.quantity}</span>
                            <span>RM {(product.unitPrice * item.quantity).toFixed(2)}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between items-center">
                        <span>Total Units:</span>
                        <span className="font-bold">{totalUnits}</span>
                      </div>
                      <div className="flex justify-between items-center font-bold">
                        <span>Total Value:</span>
                        <span>RM {totalValue.toFixed(2)}</span>
                      </div>
                    </div>
                    <Button className="w-full bg-green-600 hover:bg-green-700">
                      View Cart
                    </Button>
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">No items added yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Tabs>
    </div>
  );
};

export default LetsOrder;
